import { ArrowRight, Brush, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ArtworkCard } from "@/components/ArtworkCard";
import { Link } from "@/lib/router";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useAsync } from "@/lib/useAsync";
import type { Artwork } from "@artlove365/shared";
import { img, imgSrcSet } from "@artlove365/shared";

/** Home: wonder first. Details (how we work, custom paintings) live on their own pages. */
export function Home() {
  const { t, tx, href } = useI18n();
  const featured = useAsync(() => api.artworks({ featured: true, limit: 5 }), []);
  const fresh = useAsync(() => api.artworks({ sort: "new", limit: 3 }), []);
  const gems = useAsync(() => api.artworks({ sort: "gems", limit: 5 }), []);
  const artists = useAsync(() => api.artists(1), []);
  const movements = useAsync(() => api.taxa("movements", 1), []);
  const hero = featured.data?.artworks[0];

  return (
    <>
      <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 md:grid-cols-2 md:py-20">
        <div>
          <p className="mb-4 text-sm font-medium uppercase tracking-widest text-gold">{t("home.eyebrow")}</p>
          <h1 className="font-serif text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">{t("home.title")}</h1>
          <p className="mt-6 max-w-xl text-lg text-muted">{t("home.subtitle")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg"><Link href={href("/gallery")}>{t("home.ctaGallery")}</Link></Button>
            <Button asChild size="lg" variant="outline"><Link href={href("/artists")}>{t("home.ctaArtists")}</Link></Button>
          </div>
        </div>
        <div>
          {hero ? (
            <Link href={href(`/artworks/${hero.slug}`)} className="block rounded-sm border-[14px] border-[#5b3a1e] bg-line shadow-2xl outline outline-4 outline-gold/70">
              <img src={img(hero.imageUrl, 800)} srcSet={imgSrcSet(hero.imageUrl, [500, 800, 1200])} sizes="(min-width: 768px) 50vw, 92vw"
                fetchPriority="high" decoding="async" width={800} height={640} alt={tx(hero.title)} className="aspect-[5/4] w-full object-cover" />
            </Link>
          ) : <div className="aspect-[5/4] rounded-sm bg-line" />}
        </div>
      </section>

      {/* Three painting blocks, each with its own reason to exist. */}
      <HomeBlock title={t("home.featured")} link={{ href: href("/gallery"), label: `${t("home.ctaGallery")} →` }}
        items={featured.data?.artworks.slice(1, 5)} cols="md:grid-cols-4" />
      <HomeBlock title={t("home.newTitle")} why={t("home.newWhy")} items={fresh.data?.artworks.slice(0, 3)} cols="md:grid-cols-3" tinted />
      <HomeBlock title={t("home.gemsTitle")} why={t("home.gemsWhy")} items={gems.data?.artworks.slice(0, 5)} cols="sm:grid-cols-3 lg:grid-cols-5" />

      {artists.data && artists.data.artists.length > 0 && (
        <section className="border-y border-line bg-surface">
          <div className="mx-auto max-w-6xl px-4 py-12">
            <div className="mb-8 flex items-end justify-between gap-4">
              <h2 className="font-serif text-3xl font-semibold">{t("home.featuredArtists")}</h2>
              <Link href={href("/artists")} className="text-sm text-accent hover:underline">{t("nav.artists")} →</Link>
            </div>
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
              {artists.data.artists.slice(0, 6).map((a) => (
                <Link key={a.slug} href={href(`/artists/${a.slug}`)} className="group text-center">
                  <div className="mx-auto aspect-square w-full overflow-hidden rounded-full border-4 border-canvas bg-line shadow-md">
                    {a.imageUrl && <img src={img(a.imageUrl, 160)} srcSet={imgSrcSet(a.imageUrl, [160, 330])} sizes="160px" width={160} height={160} decoding="async" alt={tx(a.nameI18n)} loading="lazy" className="size-full object-cover object-[50%_25%] transition-transform duration-500 group-hover:scale-105" />}
                  </div>
                  <p className="mt-3 font-serif text-lg font-semibold leading-tight group-hover:text-accent">{tx(a.nameI18n)}</p>
                  <p className="text-xs text-muted">{a.years}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {movements.data && movements.data.taxa.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 py-12">
          <h2 className="font-serif text-3xl font-semibold">{t("home.browseMovements")}</h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {movements.data.taxa.slice(0, 12).map((m) => (
              <Link key={m.slug} href={href(`/movements/${m.slug}`)} className="rounded-full border border-line bg-surface px-4 py-2 text-sm hover:border-accent hover:text-accent">
                {tx(m.name)} <span className="text-muted">{m.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-6xl px-4 pb-16 pt-4">
        <h2 className="mb-6 font-serif text-3xl font-semibold">{t("home.discover")}</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {([[Brush, "/how-it-works", "home.howCardTitle", "home.howCardBody"], [Camera, "/custom", "home.customCardTitle", "home.customCardBody"]] as const).map(([Icon, to, title, body]) => (
            <Link key={to} href={href(to)} className="group rounded-xl border border-line bg-surface p-8 transition hover:border-accent hover:shadow-lg">
              <Icon className="size-7 text-accent" />
              <h3 className="mt-4 font-serif text-2xl font-semibold">{t(title)}</h3>
              <p className="mt-2 text-muted">{t(body)}</p>
              <span className="mt-4 inline-flex items-center gap-1 font-medium text-accent">{t("common.readMore")} <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" /></span>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}

function HomeBlock({ title, why, items, cols, link, tinted }: {
  title: string; why?: string; items?: Artwork[]; cols: string; link?: { href: string; label: string }; tinted?: boolean;
}) {
  if (items && !items.length) return null;
  return (
    <section className={tinted ? "border-y border-line bg-surface" : ""}>
      <div className="mx-auto max-w-6xl px-4 py-12">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl font-semibold">{title}</h2>
            {why && <p className="mt-2 max-w-2xl text-muted">{why}</p>}
          </div>
          {link && <Link href={link.href} className="text-sm text-accent hover:underline">{link.label}</Link>}
        </div>
        <div className={`grid grid-cols-2 gap-6 ${cols}`}>
          {items?.map((a, i) => <ArtworkCard key={a.slug} art={a} index={i} />)}
        </div>
      </div>
    </section>
  );
}
